import { CURRENT_USER_KEY, useGetCurrentUser, useGetCustomStyles, useGetPortalSummary } from 'api/hooks';
import { webAppAPI } from 'api/web-app';
import { ReactComponent as DefaultCompanyLogo } from 'assets/defaultImages/CompanyLogo.svg';
import DefaultBanner from 'assets/defaultImages/GenericBanner.png';
import { Loading } from 'Components/Loading';
import { Formik, FormikHelpers } from 'formik';
import * as React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { colors } from 'styles/colors';
import { trigger } from 'swr';
import * as yup from 'yup';
import { FailureModal } from './FailureModal';
import './Login.css';
import { SoloButton } from './SoloButton';
import { SoloFormInput } from './SoloFormField';
import { SuccessModal } from './SuccessModal';

type LoginValues = {
  username: string;
  password: string;
};
const smallNavLinkStyles = {
  color: colors.septemberGrey
};

const initialValues: LoginValues = {
  username: '',
  password: ''
};
const validationSchema = yup.object().shape({
  username: yup.string().required('A username is required'),
  password: yup.string().required('The password is required')
});

export const Login = () => {
  const routerHistory = useHistory();
  const [loginResolved, setLoginResolved] = React.useState<{ wasSuccessful: boolean; errorMessage?: string } | null>(
    null
  );

  const { currentUser, currentUserError } = useGetCurrentUser();
  const { customStyles, customStylesError } = useGetCustomStyles();
  const { portalSummary, portalSummaryError, hasOIDCLoginEnabled } = useGetPortalSummary();
  const [showBasicAuthCredentials, setShowBasicAuthCredentials] = React.useState(!hasOIDCLoginEnabled);

  if (!!currentUser) {
    routerHistory.push({ pathname: '/' });
  }

  React.useEffect(() => {
    if (!!portalSummary) {
      setShowBasicAuthCredentials(!hasOIDCLoginEnabled);
    }
  }, [hasOIDCLoginEnabled]);

  const attemptLogin = (values: LoginValues, formikHelpers: FormikHelpers<LoginValues>) => {
    webAppAPI
      .login({ basicAuth: values })
      .then(result => {
        if (!!result) {
          if (result.isFirstLogin) {
            routerHistory.push({ pathname: '/reset-password/' });
          } else {
            trigger(CURRENT_USER_KEY);
          }
        } else {
          formikHelpers?.resetForm();
        }
        setLoginResolved({ wasSuccessful: !!result, errorMessage: 'Problems logging in' });

        setTimeout(() => {
          setLoginResolved(null);
        }, 3100);
      })
      .catch(() => {
        setLoginResolved({ wasSuccessful: false, errorMessage: 'Problems logging in' });
      });
  };

  if (!portalSummary && !portalSummaryError) {
    return <Loading center>Loading...</Loading>;
  }

  return (
    <div className='login-page-container'>
      <div className='login-page-background-image-container'>
        <img className='object-cover ' src={customStyles?.bannerUrl || DefaultBanner} alt={'banner image'} />
        <div className='login-page-background-image-container-overlay' />
      </div>

      <div className='login-page-modal-container'>
        <div className='login-page-modal-card'>
          <div className='login-page-logo-container'>
            {!!customStyles?.logoUrl ? <img src={customStyles?.logoUrl} alt={''} /> : <DefaultCompanyLogo />}
          </div>
          <div className='login-page-modal-title'>Log in to the Portal</div>

          <Formik<LoginValues>
            initialValues={initialValues}
            onSubmit={attemptLogin}
            validationSchema={validationSchema}>
            {formik => (
              <div
                className='login-page-form-container'
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    formik.handleSubmit();
                  }
                }}>
                {showBasicAuthCredentials ? (
                  <>
                    <div className='login-page-form-row'>
                      <SoloFormInput name='username' autoComplete='username' placeholder='Username' />
                    </div>
                    <div className='login-page-form-row'>
                      <SoloFormInput name='password' autoComplete='current-password' placeholder='Password' password />
                    </div>

                    <SoloButton
                      text='Log In'
                      disabled={(!formik.isValid && formik.dirty) || !formik.dirty}
                      onClick={formik.handleSubmit}
                    />
                    {!!hasOIDCLoginEnabled && (
                      <a href={portalSummary?.oidcLoginUrl} rel='noreferrer noopener'>
                        <div
                          style={{
                            ...(!!customStyles?.customStyling?.primaryColor && {
                              color: customStyles?.customStyling?.primaryColor
                            })
                          }}
                          onClick={() => setShowBasicAuthCredentials(false)}
                          className=' login-page-form-row login-link-text'>
                          Log in using OpenID Connect
                        </div>
                      </a>
                    )}
                  </>
                ) : (
                  <>
                    {!!hasOIDCLoginEnabled && (
                      <div className=' login-page-form-row'>
                        <a href={portalSummary?.oidcLoginUrl} rel='noreferrer noopener'>
                          <SoloButton text='Log in with OpenID Connect' onClick={() => {}} />
                        </a>
                      </div>
                    )}
                    {!portalSummary?.basicAuthLoginEnabled ? (
                      <div className='flex items-center justify-center'> Basic Auth is not enabled</div>
                    ) : (
                      <div
                        style={{
                          ...(!!customStyles?.customStyling?.primaryColor && {
                            color: customStyles?.customStyling?.primaryColor
                          })
                        }}
                        onClick={() => setShowBasicAuthCredentials(true)}
                        className=' login-page-form-row login-link-text'>
                        Log in using credentials
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </Formik>
        </div>
      </div>

      <SuccessModal
        visible={!!loginResolved?.wasSuccessful}
        successMessage={
          <div>
            Logged in successfully.{' '}
            <div>
              You will now be redirected to the{' '}
              <Link style={smallNavLinkStyles} to='/'>
                home page
              </Link>
              .
            </div>
          </div>
        }
      />

      <FailureModal
        visible={!!loginResolved && !loginResolved.wasSuccessful}
        failureMessage={<div>Trouble logging in.</div>}
        minorMessage={loginResolved?.errorMessage}
      />
    </div>
  );
};
